import { Loader2, Mail } from 'lucide-react';
import type { InboxScanState } from '@shared/firstClaim';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

interface InboxScanProgressProps {
  state: InboxScanState;
  onAddManually?: () => void;
}

/** Share of the scan done, 0-100, or null when the pipeline does not know the total. */
export function scanPercent(state: Pick<InboxScanState, 'scannedCount' | 'totalCount'>): number | null {
  const { scannedCount, totalCount } = state;
  if (scannedCount === undefined || !totalCount || totalCount <= 0) return null;
  return Math.min(100, Math.round((scannedCount / totalCount) * 100));
}

export function InboxScanProgress({ state, onAddManually }: InboxScanProgressProps) {
  const percent = scanPercent(state);
  const scanned = state.scannedCount ?? 0;

  return (
    <Card data-testid="inbox-scan-progress" role="status" aria-live="polite">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
          Scanning your inbox for claims
        </CardTitle>
        <CardDescription>
          {state.accountEmail ? `Reading emails in ${state.accountEmail}. ` : ''}
          ClaimHive looks for carrier claim numbers, policyholder names, dates of loss and attachments. You can keep
          working while it runs.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {percent === null ? (
          <div className="relative h-2 w-full overflow-hidden rounded-full bg-secondary" data-testid="inbox-scan-indeterminate">
            <div className="absolute inset-y-0 w-1/3 animate-pulse rounded-full bg-primary" />
          </div>
        ) : (
          <Progress
            value={percent}
            className="h-2"
            aria-label="Inbox scan progress"
            data-testid="inbox-scan-bar"
          />
        )}
        <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="inbox-scan-count">
          <Mail className="h-4 w-4" aria-hidden="true" />
          {scanned > 0
            ? `${scanned.toLocaleString()} email${scanned === 1 ? '' : 's'} checked${percent === null ? '' : ` (${percent}%)`}`
            : 'Starting the scan…'}
        </p>
        {onAddManually && (
          <Button variant="outline" className="w-full sm:w-auto" onClick={onAddManually} data-testid="scan-add-claim-manually">
            Add a claim manually instead
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
